<script setup lang="ts">
import Button from 'primevue/button'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import StatusStepper from '@/components/order/StatusStepper.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import { useScrollLock } from '@/composables/useScrollLock'
import { stepsOf } from '@/constants/mockOrderStatus'
import { noticeOf } from '@/constants/orderNotice'
import { ROUTE } from '@/constants/routes'
import { useOrderNoticeStore } from '@/stores/orderNotice'
import { SHOW_DELIVERY_TIME } from '@/constants/features'

/**
 * Карточка о смене статуса заказа («Уведомление / …» в макете): картинка
 * выступает над шитом, под ней время, заголовок, подпись, степпер и кнопка
 * в сам заказ.
 *
 * Показываем на главной: наблюдение включает она же, поэтому карточка не
 * перехватывает клиента посреди корзины или оформления. Пушей у мини-аппа
 * нет, и это единственный способ рассказать о смене статуса.
 */
const { t } = useI18n()

const router = useRouter()
const store = useOrderNoticeStore()

const notice = computed(() => store.notice)

const content = computed(() =>
  notice.value ? noticeOf(notice.value.step, notice.value.isPickup) : null,
)

const steps = computed(() => stepsOf(notice.value?.isPickup ? 'pickup' : 'delivery'))

const isVisible = computed(() => Boolean(notice.value))

useScrollLock(isVisible)

function close(): void {
  store.dismiss()
}

function track(): void {
  const orderId = notice.value?.orderId
  store.dismiss()

  if (orderId) void router.push(`${ROUTE.orders}/${orderId}`)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="order-notice">
      <div v-if="notice && content" class="order-notice" @click.self="close">
        <section class="order-notice__sheet">
          <button
            type="button"
            class="order-notice__close"
            :aria-label="t('common.close')"
            @click="close"
          >
            <AppIcon name="x" :size="20" />
          </button>

          <!-- Картинка выходит за верхний край шита, как в макете -->
          <img :src="content.image" :alt="content.title" class="order-notice__image" />

          <!-- У доставленного заказа времени уже нет — пилюлю не рисуем -->
          <span v-if="SHOW_DELIVERY_TIME && notice.eta" class="order-notice__eta">
            ~{{ notice.eta }}
            <span class="order-notice__eta-unit">{{ t('units.min') }}</span>
          </span>

          <h2 class="order-notice__title">{{ content.title }}</h2>
          <p class="order-notice__text font-14-m">{{ content.text }}</p>

          <StatusStepper :steps="steps" :current="notice.step" class="order-notice__steps" />

          <Button class="order-notice__track" @click="track">
            {{ t('checkout.successTrackNumber', { number: notice.number }) }}
          </Button>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.order-notice {
  position: fixed;
  inset: 0;
  z-index: var(--z-sheet);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgb(0 0 0 / 40%);

  &__sheet {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: var(--app-max-width);
    /* 120 сверху — под картинку, она наполовину лежит выше края шита */
    padding: 120px var(--screen-padding) calc(29px + var(--tg-safe-area-bottom));
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
    text-align: center;
  }

  /* 200px — высота из макета; половина картинки лежит выше края шита */
  &__image {
    position: absolute;
    top: -100px;
    height: 200px;
    object-fit: contain;
  }

  /* Крестик в макете — скруглённый квадрат 40 r14, а не круг */
  &__close {
    border-radius: 14px;
  }

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    background: var(--surface-muted);
    color: var(--icon-color);
    cursor: pointer;
  }

  /* Пилюля времени 113×49: «~42» крупно, единица мельче */
  &__eta {
    display: flex;
    align-items: baseline;
    gap: 4px;
    padding: 6px 14px;
    border-radius: var(--radius-pill);
    background: var(--surface-inverse);
    color: var(--text-color-inverse);
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  &__eta-unit {
    font-size: 14px;
  }

  &__title {
    margin-top: 20px;
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  &__text {
    color: var(--text-color-secondary);
    line-height: 24px;
    white-space: pre-line;
  }

  /* Степпер занимает всю ширину шита — иначе шаги сбиваются в кучу */
  &__steps {
    align-self: stretch;
    margin-top: 20px;
  }

  &__track {
    width: 100%;
    margin-top: 32px;
  }

  &-enter-active,
  &-leave-active {
    transition: opacity 0.25s ease;

    .order-notice__sheet {
      transition: transform 0.25s ease;
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .order-notice__sheet {
      transform: translateY(20px);
    }
  }
}
</style>
